import React from 'react';
import { businessData } from '@/config/businessData';

const COMPANY_DATA = {
  ...businessData,
  phone: businessData.contact.phoneLink,
  email: businessData.contact.email,
  socials: Object.values(businessData.socials)
};

const JsonLd = ({ type, data }) => {
  let schema = null;

  switch (type) {
    case 'Organization':
      schema = {
        "@context": "https://schema.org",
        "@type": "Organization",
        "@id": `${COMPANY_DATA.url}/#organization`,
        "name": COMPANY_DATA.name,
        "url": COMPANY_DATA.url,
        "logo": {
          "@type": "ImageObject",
          "url": COMPANY_DATA.logo,
          "width": "600",
          "height": "120"
        },
        "contactPoint": {
          "@type": "ContactPoint",
          "telephone": COMPANY_DATA.phone,
          "contactType": "customer service",
          "email": COMPANY_DATA.email,
          "areaServed": "IN",
          "availableLanguage": ["English", "Hindi"]
        },
        "sameAs": COMPANY_DATA.socials,
        "foundingDate": COMPANY_DATA.founded
      };
      break;

    case 'LocalBusiness':
      schema = {
        "@context": "https://schema.org",
        "@type": "LocalBusiness",
        "@id": `${COMPANY_DATA.url}/#localbusiness`,
        "name": COMPANY_DATA.name,
        "image": COMPANY_DATA.logo,
        "url": COMPANY_DATA.url,
        "telephone": COMPANY_DATA.phone,
        "email": COMPANY_DATA.email,
        "address": {
          "@type": "PostalAddress",
          "streetAddress": COMPANY_DATA.address.street,
          "addressLocality": COMPANY_DATA.address.city,
          "addressRegion": COMPANY_DATA.address.state,
          "postalCode": COMPANY_DATA.address.zip,
          "addressCountry": COMPANY_DATA.address.country
        },
        "geo": {
          "@type": "GeoCoordinates",
          "latitude": "28.9288",
          "longitude": "77.1030"
        },
        "areaServed": {
          "@type": "Country",
          "name": "India"
        },
        "openingHoursSpecification": {
          "@type": "OpeningHoursSpecification",
          "dayOfWeek": [
            "Monday",
            "Tuesday",
            "Wednesday",
            "Thursday",
            "Friday",
            "Saturday"
          ],
          "opens": "09:00",
          "closes": "18:00"
        },
        "priceRange": "$$"
      };
      break;

    case 'BreadcrumbList':
      schema = {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": data.map((item, index) => ({
          "@type": "ListItem",
          "position": index + 1,
          "name": item.name,
          "item": item.url.startsWith('http') ? item.url : `${COMPANY_DATA.url}${item.url}`
        }))
      };
      break;

    case 'Service':
      schema = {
        "@context": "https://schema.org",
        "@type": "Service",
        "name": data.name,
        "description": data.description,
        "serviceType": data.serviceType || data.name,
        "provider": {
          "@type": "LocalBusiness",
          "@id": `${COMPANY_DATA.url}/#localbusiness`
        },
        "areaServed": {
          "@type": "Country",
          "name": "India"
        },
        "category": data.category || "Label Manufacturing",
        "url": data.url || `${COMPANY_DATA.url}/services`,
        "image": data.image || COMPANY_DATA.logo,
        "offers": {
          "@type": "Offer",
          "availability": "https://schema.org/InStock",
          "priceCurrency": "INR"
        }
      };
      if (data.mainEntityOfPage) {
        schema.mainEntityOfPage = {
          "@type": "WebPage",
          "@id": data.mainEntityOfPage
        };
      }
      break;

    case 'OfferCatalog':
      schema = {
        "@context": "https://schema.org",
        "@type": "OfferCatalog",
        "name": data.name,
        "itemListElement": data.items.map(item => ({
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": item.name,
            "description": item.description,
            "url": item.url.startsWith('http') ? item.url : `${COMPANY_DATA.url}${item.url}`
          }
        }))
      };
      break;

    case 'VideoObject':
      schema = {
        "@context": "https://schema.org",
        "@type": "VideoObject",
        "name": data.name,
        "description": data.description,
        "thumbnailUrl": data.thumbnailUrl,
        "uploadDate": data.uploadDate,
        "duration": data.duration,
        "contentUrl": data.contentUrl,
        "embedUrl": data.embedUrl,
        "interactionStatistic": {
          "@type": "InteractionCounter",
          "interactionType": { "@type": "WatchAction" },
          "userInteractionCount": data.viewCount || 1000
        }
      };
      break;

    case 'FAQPage':
      schema = {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": data.map(item => ({
          "@type": "Question",
          "name": item.question,
          "acceptedAnswer": {
            "@type": "Answer",
            "text": item.answer
          }
        }))
      };
      break;

    case 'BlogPosting':
      schema = {
        "@context": "https://schema.org",
        "@type": "BlogPosting",
        "mainEntityOfPage": {
          "@type": "WebPage",
          "@id": data.url
        },
        "headline": data.title,
        "description": data.description,
        "image": data.image,
        "author": {
          "@type": "Organization",
          "name": COMPANY_DATA.name,
          "url": COMPANY_DATA.url
        },
        "publisher": {
          "@type": "Organization",
          "name": COMPANY_DATA.name,
          "logo": {
            "@type": "ImageObject",
            "url": COMPANY_DATA.logo
          }
        },
        "datePublished": data.datePublished,
        "dateModified": data.dateModified || data.datePublished
      };
      break;

    default:
      schema = data;
  }

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
};

export default JsonLd;
